<template>
  <div class="col-12">
    <div class="card-body px-0 pb-2">
      <div class="p-4">
        <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
          <div class="row g-3">
            
            <!-- Personal Information -->
            <div class="col-md-6">
              <label for="username" class="bmd-label-float">{{ t('guardians.fields.username') }}</label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.username }"
                id="username"
                v-model.trim="formData.username"
                required
                @blur="validateField('username')"
                :placeholder="t('guardians.placeholders.username')"
              />
              <div class="invalid-feedback">{{ errors.username }}</div>
            </div>

            <div class="col-md-6">
              <label for="email" class="bmd-label-float">{{ t('guardians.fields.email') }}</label>
              <input
                type="email"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.email }"
                id="email"
                v-model.trim="formData.email"
                @blur="validateEmail()"
                :placeholder="t('guardians.placeholders.email')"
              />
              <div class="invalid-feedback">{{ errors.email }}</div>
            </div>

            <!-- Password Fields -->
            <div class="col-md-6">
              <label for="password" class="bmd-label-float">{{ t('guardians.fields.password') }}</label>
              <input
                type="password"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.password }"
                id="password"
                v-model="formData.password"
                :required="!isEditMode"
                @blur="validatePassword()"
                :placeholder="t('guardians.placeholders.password')"
              />
              <div class="invalid-feedback">{{ errors.password }}</div>
              <small v-if="isEditMode" class="text-muted">{{ t('guardians.notes.leaveEmptyIfNoChange') }}</small>
            </div>

            <div class="col-md-6">
              <label for="password_confirmation" class="bmd-label-float">{{ t('guardians.fields.passwordConfirmation') }}</label>
              <input
                type="password"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.password_confirmation }"
                id="password_confirmation"
                v-model="formData.password_confirmation"
                :required="!isEditMode"
                @blur="validateConfirmPassword()"
                :placeholder="t('guardians.placeholders.passwordConfirmation')"
              />
              <div class="invalid-feedback">{{ errors.password_confirmation }}</div>
            </div>

            <!-- Gender -->
            <div class="col-md-6">
              <label for="gender" class="bmd-label-float">{{ t('guardians.fields.gender') }}</label>
              <select
                class="form-control borderless-input"
                id="gender"
                v-model="formData.gender"
                required
              >
                <option value="M">{{ t('guardians.options.male') }}</option>
                <option value="F">{{ t('guardians.options.female') }}</option>
              </select>
            </div>

            <!-- Nationality -->
            <div class="col-md-6">
              <label for="nationality_id" class="bmd-label-float">{{ t('guardians.fields.nationality') }}</label>
              <select
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.nationality_id }"
                id="nationality_id"
                v-model="formData.nationality_id"
                required
                @change="handleNationalityChange"
              >
                <option value="">{{ t('guardians.placeholders.selectNationality') }}</option>
                <option v-if="isNationalitiesLoading" disabled>{{ t('guardians.placeholders.loadingNationalities') }}</option>
                <option
                  v-else
                  v-for="nationality in nationalities"
                  :key="nationality.id"
                  :value="nationality.id"
                >
                  {{ localizedNatName(nationality) }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.nationality_id }}</div>
            </div>

            <!-- National Number -->
            <div class="col-md-6">
              <label for="national_num" class="bmd-label-float">
                {{ t('guardians.fields.nationalNumber') }}
                <span v-if="isLibyan" class="text-danger">*</span>
                <span v-else class="text-muted">({{ t('guardians.notes.libyansOnly') }})</span>
              </label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.national_num }"
                id="national_num"
                v-model.trim="formData.national_num"
                :required="isLibyan"
                :disabled="!isLibyan"
                @blur="validateNationalNumber()"
                :placeholder="isLibyan ? t('guardians.placeholders.requiredForLibyans') : t('guardians.placeholders.libyansOnly')"
              />
              <div class="invalid-feedback">{{ errors.national_num }}</div>
            </div>

            <!-- Passport Number -->
            <div class="col-md-6">
              <label for="passport_num" class="bmd-label-float">
                {{ t('guardians.fields.passportNumber') }}
                <span v-if="!isLibyan" class="text-danger">*</span>
                <span v-else class="text-muted">({{ t('guardians.notes.nonLibyansOnly') }})</span>
              </label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.passport_num }"
                id="passport_num"
                v-model.trim="formData.passport_num"
                :required="!isLibyan"
                :disabled="isLibyan"
                @blur="validatePassport()"
                :placeholder="!isLibyan ? t('guardians.placeholders.requiredForNonLibyans') : t('guardians.placeholders.nonLibyansOnly')"
              />
              <div class="invalid-feedback">{{ errors.passport_num }}</div>
            </div>

            <!-- Contact -->
            <div class="col-md-6">
              <label for="phone_num" class="bmd-label-float">{{ t('guardians.fields.phone') }}</label>
              <input
                type="tel"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.phone_num }"
                id="phone_num"
                v-model.trim="formData.phone_num"
                required
                @blur="validatePhone()"
                :placeholder="t('guardians.placeholders.phone')"
              />
              <div class="invalid-feedback">{{ errors.phone_num }}</div>
            </div>

            <div class="col-md-6">
              <label for="sec_phone_num" class="bmd-label-float">{{ t('guardians.fields.secondaryPhone') }}</label>
              <input
                type="tel"
                class="form-control outline-input"
                id="sec_phone_num"
                v-model.trim="formData.sec_phone_num"
                :placeholder="t('guardians.placeholders.secondaryPhone')"
              />
            </div>

            <!-- Address & Occupation -->
            <div class="col-md-6">
              <label for="address" class="bmd-label-float">{{ t('guardians.fields.address') }}</label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.address }"
                id="address"
                v-model.trim="formData.address"
                required
                @blur="validateAddress()"
                :placeholder="t('guardians.placeholders.address')"
              />
              <div class="invalid-feedback">{{ errors.address }}</div>
            </div>

            <div class="col-md-6">
              <label for="job" class="bmd-label-float">{{ t('guardians.fields.job') }}</label>
              <input
                type="text"
                class="form-control outline-input"
                id="job"
                v-model.trim="formData.job"
                :placeholder="t('guardians.placeholders.job')"
              />
            </div>

          <div class="col-md-6">
  <label for="qualification" class="bmd-label-float">{{ t('guardians.fields.qualification') }}</label>
  <select
    class="form-select"
    :class="{ 'is-invalid': errors.qualification }"
    id="qualification"
    v-model="formData.qualification"
    @blur="validateField('qualification')"
  >
    <option value="">{{ t('guardians.placeholders.selectQualification') }}</option>
    <option v-for="qual in qualifications" :key="qual" :value="qual">
      {{ qual }}
    </option>
  </select>
  <div class="invalid-feedback">{{ errors.qualification }}</div>
</div>
            <div class="col-md-6">
              <label for="register_date" class="bmd-label-float">{{ t('guardians.fields.registerDate') }}</label>
              <input
                type="date"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.register_date }"
                id="register_date"
                v-model="formData.register_date"
                required
                @blur="validateRegisterDate()"
                :max="maxDate"
              />
              <div class="invalid-feedback">{{ errors.register_date }}</div>
            </div>

          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, watch, defineEmits, defineProps, defineExpose } from "vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import api from "@/api";

const { t, locale } = useI18n(); 

const props = defineProps({
  title: String,
  submitText: {
    type: String,
    default: "حفظ"
  },
  loading: {
    type: Boolean,
    default: false
  },
  isEditMode: {
    type: Boolean,
    default: false
  },
  initialData: {
    type: Object,
    default: () => ({
      username: '',
      email: '',
      password: '',
      password_confirmation: '',
      gender: 'M',
      nationality_id: '',
      national_num: '',
      passport_num: '',
      phone_num: '',
      sec_phone_num: '',
      address: '',
      job: '',
      qualification: '',
      is_active: false,
      register_date: new Date().toISOString().split('T')[0]
    })
  }
});
const qualifications = ref([
  t('guardians.qualifications.bachelor'),
  t('guardians.qualifications.master'),
  t('guardians.qualifications.phd'),
  t('guardians.qualifications.diploma'),
  t('guardians.qualifications.highSchool')
]);
const emit = defineEmits(['submit', 'cancel', 'validation-error', 'update:form-data']);
const toast = useToast();

// Form data and state
const formData = ref({ ...props.initialData });
const errors = ref({});
const isSubmitting = ref(false);
const nationalities = ref([]);
const isNationalitiesLoading = ref(false);

// Computed
const isLibyan = computed(() => {
  if (!formData.value.nationality_id || nationalities.value.length === 0) 
    return false;
  const selectedNationality = nationalities.value.find(
    n => n.id.toString() === formData.value.nationality_id.toString()
  );
  return selectedNationality?.code === "LY";
});

const maxDate = computed(() => new Date().toISOString().split('T')[0]);

// Validation functions with i18n
const validateField = (field) => {
  if (!formData.value[field]) {
    errors.value[field] = t('guardians.validation.required');
    return false;
  }
  delete errors.value[field];
  return true;
};

const validateEmail = () => {
  if (formData.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
    errors.value.email = t('guardians.validation.invalidEmail');
    return false;
  }
  delete errors.value.email;
  return true;
};

const validatePassword = () => {
  if (!props.isEditMode && !formData.value.password) {
    errors.value.password = t('guardians.validation.required');
    return false;
  }
  if (formData.value.password && formData.value.password.length < 8) {
    errors.value.password = t('guardians.validation.passwordMin', { min: 8 });
    return false;
  }
  delete errors.value.password;
  return true;
};

const validateConfirmPassword = () => {
  if (formData.value.password !== formData.value.password_confirmation) {
    errors.value.password_confirmation = t('guardians.validation.passwordMismatch');
    return false;
  }
  delete errors.value.password_confirmation;
  return true;
};

const validatePhone = () => {
  if (!formData.value.phone_num) {
    errors.value.phone_num = t('guardians.validation.required');
    return false;
  }
  if (!/^[\d\s+-]{7,15}$/.test(formData.value.phone_num)) {
    errors.value.phone_num = t('guardians.validation.invalidPhone');
    return false;
  }
  delete errors.value.phone_num;
  return true;
};

const validateAddress = () => {
  if (!formData.value.address) {
    errors.value.address = t('guardians.validation.required');
    return false;
  }
  delete errors.value.address;
  return true;
};

const validateNationalNumber = () => {
  if (isLibyan.value && !formData.value.national_num) {
    errors.value.national_num = t('guardians.validation.requiredForLibyans');
    return false;
  }
  if (isLibyan.value && !/^\d{12}$/.test(formData.value.national_num)) {
    errors.value.national_num = t('guardians.validation.nationalNumInvalid');
    return false;
  }
  delete errors.value.national_num;
  return true;
};

const validatePassport = () => {
  if (!isLibyan.value && !formData.value.passport_num) {
    errors.value.passport_num = t('guardians.validation.requiredForNonLibyans');
    return false;
  }
  if (!isLibyan.value && !/^[a-zA-Z0-9]{6,20}$/.test(formData.value.passport_num)) {
    errors.value.passport_num = t('guardians.validation.passportInvalid');
    return false;
  }
  delete errors.value.passport_num;
  return true;
};

const validateRegisterDate = () => {
  if (!formData.value.register_date) {
    errors.value.register_date = t('guardians.validation.required');
    return false;
  }
  delete errors.value.register_date;
  return true;
};

// Methods
const fetchNationalities = async () => {
  isNationalitiesLoading.value = true;
  try {
    const response = await api.get('/nationalities', {
      params: { locale: (locale.value || 'ar').split('-')[0] }
    });
    nationalities.value = response.data.data;
  } catch (e) {
    console.error('Error fetching nationalities:', e);
    nationalities.value = [];
  } finally {
    isNationalitiesLoading.value = false;
  }
};
const localizedNatName = (nat) => {
  if (!nat) return '';
  const l = (locale.value || 'en').split('-')[0]; // ar-YE → ar
  return nat.translations?.[l] ?? nat.translations?.en ?? nat.name ?? '';
};

const handleNationalityChange = () => {
  if (!isLibyan.value) formData.value.national_num = "";
  else formData.value.passport_num = "";
};

const validateForm = () => {
  errors.value = {};
  const validations = [
    validateField('username'),
    validateEmail(),
    validateField('gender'),
    validateField('nationality_id'),
    validateNationalNumber(),
    validatePassport(),
    validatePhone(),
    validateAddress(),
    validateRegisterDate(),
  ];
  if (!props.isEditMode) {
    validations.push(validatePassword(), validateConfirmPassword());
  }
  return validations.every(valid => valid);
};

const handleSubmit = () => {
  if (!validateForm()) {
    emit('validation-error');
    toast.warning(t('guardians.toast.fixErrors'));
    return;
  }

  const data = { ...formData.value };
  if (props.isEditMode && !data.password) {
    delete data.password;
    delete data.password_confirmation;
  }

  isSubmitting.value = true;
  try {
    emit('submit', data);
  } finally {
    isSubmitting.value = false;
  }
};

const submitForm = () => handleSubmit();

defineExpose({ submitForm });

// Lifecycle
onMounted(() => fetchNationalities());

// Watchers
watch(() => props.initialData, (newData) => {
  formData.value = { ...newData, password: '', password_confirmation: '' };
}, { immediate: true, deep: true });

watch(formData, (newVal) => emit('update:form-data', newVal), { deep: true });
</script>
